import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { StringParam, useQueryParams } from 'use-query-params'

import { RedactAccountModal } from '../../accounts/components/redact-account'
import { RemoveAccountModal } from '../../accounts/components/remove-account'
import { ReactivateAccountModal } from '../../accounts/components/reactivate-account'

import {
  type AccountModifyAction,
  useAccountAccess,
  usePermissions,
  useRedactAccountModal,
  useRequiredParams,
  useReset2FAModal
} from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useToast } from '@percent/admin-dashboard/containers/toast/ToastContext'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import { AccountListTable, Account, Reset2FAModal } from '@percent/domain/iam'
import { useQueryList, useMutation } from '@percent/utility'
import { SearchInput, Flex, Alert, Button, Badge } from '@percent/lemonade'
import { useAccountListSearch } from '@percent/admin-dashboard/app/accounts/utils/hooks/useAccountListSearch'
import { PartnerScopes } from '@percent/admin-dashboard/constants/scopes'
import { AssignRoleDialog } from '@percent/admin-dashboard/app/member/AssignRoleDialog/AssignRoleDialog'
import { DeleteMemberDialog } from '@percent/admin-dashboard/app/member/deleteMemberDialog/DeleteMemberDialog'
import { CreateMemberDialog } from '@percent/admin-dashboard/app/member/createMemberDialog/CreateMemberDialog'
import { MemberListTableAccountDetails } from '@percent/admin-dashboard/app/member/MemberListTable.types'
import type { SectionItem, SectionProps } from '@percent/lemonade'
import { MEMBER_PROFILE_VIEW_PERMISSIONS } from '@percent/admin-dashboard/app/memberProfile/memberProfileAccess'
import { useDeleteAccountModal } from '@percent/admin-dashboard/common/hooks/useDeleteAccountModal'
import { useReactivateAccountModal } from '@percent/admin-dashboard/common/hooks/useReactivateAccountModal'

type PartnerMembersProps = {
  ssoScope?: PartnerScopes
}

const isDefined = <T,>(value: T | undefined | null): value is T => !!value

export function PartnerMembers({ ssoScope }: PartnerMembersProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: partnerId } = useRequiredParams()
  const { accountService, adminService } = useServices()
  const addToast = useToast()
  const [queryParams, setQueryParams] = useQueryParams({
    search: StringParam
  })
  const { userHasPermissions } = usePermissions()
  const { canViewAccount, canModifyAccount } = useAccountAccess()

  const [assignRoleToAccount, setAssignRoleToAccount] = useState<MemberListTableAccountDetails | undefined>(undefined)
  const [removeFromPartnerAccount, setRemoveFromPartnerAccount] = useState<{ id: string; email: string } | undefined>(
    undefined
  )
  const [isCreateMemberOpen, setIsCreateMemberOpen] = useState(false)

  const [{ data, isLoading, totalResults, errorMessage, status }, { nextPage, previousPage, refresh }] = useQueryList(
    ['/accounts', partnerId],
    accountService.getAccounts,
    {
      partnerId,
      ...(queryParams.search && { search: queryParams.search })
    }
  )

  const {
    selectedAccount: reset2FAAccount,
    isModalOpen: isReset2FAModalOpen,
    openModal: openReset2FAModal,
    closeModal: closeReset2FAModal,
    reset2FA
  } = useReset2FAModal({ onSuccess: refresh })

  const {
    selectedAccount: redactAccount,
    isModalOpen: isRedactModalOpen,
    openModal: openRedactModal,
    closeModal: closeRedactModal
  } = useRedactAccountModal()

  const {
    selectedAccount: deleteAccount,
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useDeleteAccountModal()

  const {
    selectedAccount: reactivateAccount,
    isModalOpen: isReactivateModalOpen,
    openModal: openReactivateModal,
    closeModal: closeReactivateModal
  } = useReactivateAccountModal()

  const isStrictSSOPartner = ssoScope === PartnerScopes.SingleSignOn

  const [{ status: resendStatus }, { mutateAsync: resendInvitation }] = useMutation(
    adminService.commands.createPartnerDeveloper
  )

  useEffect(() => {
    if (resendStatus === 'success') {
      addToast(t('typography.invitationResent'), 'success')
    }
  }, [resendStatus, addToast, t])

  const { searchValue, handleKeyChange, handleKeyPress, handleClearValue, searchInputStatusMessage } =
    useAccountListSearch({
      queryParams,
      setQueryParams
    })

  const menuItem = (key: string, label: string, onClick: () => void, visible = true): SectionItem | undefined =>
    visible ? { key, label, onClick } : undefined

  type MemberRow = Pick<
    Account,
    | 'id'
    | 'fullName'
    | 'email'
    | 'partnerId'
    | 'organisationId'
    | 'admin'
    | 'status'
    | 'role'
    | 'twoFaSecretVerifiedAt'
    | 'redactedAt'
  >

  const can = (account: MemberRow, action: AccountModifyAction): boolean => canModifyAccount(account, action)

  // The profile route requires its own permission on top of seeing the account
  const canViewProfile = (account: MemberRow): boolean =>
    userHasPermissions(MEMBER_PROFILE_VIEW_PERMISSIONS.partner) && canViewAccount(account)

  const getMenuItems = (account: MemberRow): SectionProps[] => [
    {
      items: [
        menuItem(
          'view-profile',
          t('memberProfile.viewProfile'),
          () => navigate(`/partners/${partnerId}/members/${account.id}`),
          canViewProfile(account)
        ),
        menuItem(
          'change-role',
          t('menu.changeRole'),
          () =>
            setAssignRoleToAccount({
              id: account.id,
              email: account.email,
              currentRoleName: account.role?.name || t('typography.member')
            }),
          userHasPermissions('partner_accounts:can_set_legacy_role')
        ),
        menuItem(
          'resend-invitation',
          t('menu.resend'),
          () => resendInvitation({ payload: { email: account.email }, partnerId }),
          !isStrictSSOPartner && account.status === 'pending'
        ),
        menuItem(
          'remove-member',
          t('menu.removeFromPartner'),
          () => setRemoveFromPartnerAccount({ id: account.id, email: account.email }),
          userHasPermissions('partner_accounts:can_remove')
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.authentication'),
      items: [
        menuItem(
          'reset-2fa',
          t('button.reset2FA'),
          () => openReset2FAModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          can(account, 'can_reset_2fa') && !!account.twoFaSecretVerifiedAt
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.membership'),
      items: [
        menuItem(
          'delete-account',
          t('account.modify.remove.button'),
          async () => openDeleteModal(account),
          can(account, 'can_delete') && account.status !== 'removed'
        ),
        menuItem(
          'reactivate-account',
          t('account.modify.reactivate.button'),
          () => openReactivateModal(account),
          can(account, 'can_reactivate') && account.status === 'removed' && !account.redactedAt
        ),
        menuItem(
          'redact-account',
          t('account.modify.redact.button'),
          () => openRedactModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          can(account, 'can_redact')
        )
      ].filter(isDefined)
    }
  ]

  if (errorMessage && status === 'error') {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      {isStrictSSOPartner && (
        <div data-testid="sso-warning" style={{ marginBottom: 16 }}>
          <Alert variant="warning" title={t('typography.partnerUsesSSO')} />
        </div>
      )}
      <AccountListTable
        accounts={data}
        totalResults={totalResults}
        nextPage={nextPage}
        previousPage={previousPage}
        isLoading={isLoading}
        getMenuItems={getMenuItems}
        extraColumns={[
          {
            key: 'role',
            header: t('typography.role'),
            render: account => <Badge variant="default">{account.role?.name ?? t('typography.member')}</Badge>,
            width: '15%'
          }
        ]}
        actionsAndFilters={
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              placeholder={t('table.accountsSearchPlaceholder')}
              value={searchValue}
              onChange={handleKeyChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearValue}
              statusMessage={searchInputStatusMessage}
              status={searchInputStatusMessage ? 'danger' : 'default'}
              width="100%"
            />
            {userHasPermissions('partner_accounts:can_invite') && (
              <Button
                data-testid="add-new-member"
                variant="primary"
                onClick={() => setIsCreateMemberOpen(true)}
                disabled={isStrictSSOPartner}
              >
                {t('button.addNewMember')}
              </Button>
            )}
          </Flex>
        }
      />
      <AssignRoleDialog
        open={!!assignRoleToAccount}
        account={assignRoleToAccount}
        partnerId={partnerId}
        refresh={refresh}
        onClose={() => setAssignRoleToAccount(undefined)}
      />
      {removeFromPartnerAccount && (
        <DeleteMemberDialog
          openDeleteModal={!!removeFromPartnerAccount}
          onClose={() => setRemoveFromPartnerAccount(undefined)}
          memberEmail={removeFromPartnerAccount?.email ?? ''}
          memberId={removeFromPartnerAccount?.id ?? ''}
          partnerId={partnerId}
          refresh={refresh}
        />
      )}
      <CreateMemberDialog
        open={isCreateMemberOpen}
        onClose={() => setIsCreateMemberOpen(false)}
        partnerId={partnerId}
        refresh={refresh}
      />
      {reset2FAAccount && (
        <Reset2FAModal
          account={reset2FAAccount}
          isOpen={isReset2FAModalOpen}
          onClose={closeReset2FAModal}
          onReset={reset2FA}
        />
      )}
      {redactAccount && (
        <RedactAccountModal
          isOpen={isRedactModalOpen}
          onClose={closeRedactModal}
          onSuccess={refresh}
          account={redactAccount}
        />
      )}
      {deleteAccount && (
        <RemoveAccountModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onSuccess={refresh}
          account={deleteAccount}
        />
      )}
      {reactivateAccount && (
        <ReactivateAccountModal
          isOpen={isReactivateModalOpen}
          onClose={closeReactivateModal}
          onSuccess={refresh}
          account={reactivateAccount}
        />
      )}
    </>
  )
}
